import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useViewNavigate } from '../../../hooks/useViewNavigate.js';
import { rocketSvg } from './shipArt.js';
import { staggerIn } from './motion.js';
import { claimSessionEntrance, onNewSession } from './sessionEntrance.js';
import { ShipHome } from './ShipHome.js';
import { ShipIntro } from './ShipIntro.js';
import { ShipTravelNotice } from './ShipTravelNotice.js';
import { useShip } from './useShip.js';
import { useShipGo } from './useShipGo.js';

const HOME_SECTIONS = '.h2-subhead-row, .h2-body > .h2-card, .dt-home-v2 > .h2-card';

/**
 * «Armá tu nave» en el Inicio (encargo 117), lo mismo para celular y compu. Decide:
 *  - si va la presentación (cuenta nueva, `pending`);
 *  - si el cuerpo es la nave (armándola) o las tarjetas de siempre («Ver el Inicio completo»);
 *  - la línea de arriba: volver a la nave si se está mirando el Inicio completo (si no, la de la rutina);
 *  - la entrada escalonada del Inicio de siempre: al volver de «¡Despegaste!» y la primera vez que se
 *    entra al Inicio en cada sesión (card #585, `sessionEntrance.ts`);
 *  - el aviso «Tu nave está en viaje» (card #585): una sola vez después del despegue, cuando ya entró
 *    el Inicio. Antes era esa línea de arriba durante cinco entradas y tapaba la rutina.
 */
export function useShipHome(opts: {
  variant: 'mobile' | 'desktop';
  cefr: string | null;
  programUnitId: string | null;
}) {
  const { variant } = opts;
  const navigate = useViewNavigate();
  const { ship, resolved, act, markSeen } = useShip();
  const go = useShipGo({ cefr: opts.cefr, programUnitId: opts.programUnitId });
  const [fullHome, setFullHome] = useState(false);
  const [launchHold, setLaunchHold] = useState(false);
  const [enterAfterLaunch, setEnterAfterLaunch] = useState(false);
  const [introOpen, setIntroOpen] = useState(false);

  useEffect(() => {
    if (ship?.status === 'pending') setIntroOpen(true);
  }, [ship?.status]);

  // «Tu nave está en viaje»: se decide al entrar al Inicio (marcarlo visto no lo apaga en la misma
  // visita). Sale cuando el Inicio ya entró: al volver del despegue, después de la entrada escalonada.
  const [travel, setTravel] = useState<boolean | null>(null);
  useEffect(() => {
    if (travel === null && ship) setTravel(ship.travel);
  }, [travel, ship]);
  const [noticeReady, setNoticeReady] = useState(false);
  const [noticeDone, setNoticeDone] = useState(false);
  const [entering, setEntering] = useState(false);
  const travelCounted = useRef(false);
  // Visto recién cuando aparece: si se va del Inicio antes, sale la próxima vez (Emmanuel, 21/10).
  const onNoticeShown = useCallback(() => {
    if (travelCounted.current) return;
    travelCounted.current = true;
    void act('travel-seen').catch((err: unknown) => console.warn('[ship] en viaje', err));
  }, [act]);

  const showShip = (ship?.status === 'active' && !fullHome) || launchHold;

  // El Inicio de siempre entra de a poco (y después, si toca, el aviso): al volver de «¡Despegaste!»
  // y —si se prende— la primera vez de cada sesión.
  const enter = useCallback(() => {
    setEntering(true);
    const scope = document.querySelector('.app') ?? document;
    void staggerIn([...scope.querySelectorAll(HOME_SECTIONS)]).then(() => {
      setEntering(false);
      setNoticeReady(true);
    });
  }, []);
  useLayoutEffect(() => {
    if (!enterAfterLaunch) return;
    setEnterAfterLaunch(false);
    claimSessionEntrance(); // el despegue ya cuenta como la de esta sesión
    enter();
  }, [enterAfterLaunch, enter]);

  // Primera vez en la sesión: se prueba en cada render hasta que el Inicio de siempre está en pantalla
  // (el Home espera sus datos antes de mostrarlo), y se decide una vez por visita.
  const sessionTried = useRef(false);
  useLayoutEffect(() => {
    if (sessionTried.current || showShip || enterAfterLaunch) return;
    if (!document.querySelector(HOME_SECTIONS)) return;
    sessionTried.current = true;
    if (claimSessionEntrance()) enter();
  });
  // Si vuelve a la app después de 30 minutos o más con el Inicio en pantalla, es otra sesión: entra.
  const showShipRef = useRef(showShip);
  showShipRef.current = showShip;
  useEffect(
    () =>
      onNewSession(() => {
        if (showShipRef.current || !document.querySelector(HOME_SECTIONS)) return;
        if (claimSessionEntrance()) enter();
      }),
    [enter],
  );

  // Entró al Inicio con el aviso pendiente (no lo llegó a ver al despegar): sale cuando terminó de
  // aparecer el Inicio (el fundido de entrada dura 320 ms).
  useEffect(() => {
    if (travel !== true || noticeReady || enterAfterLaunch || entering || showShip) return;
    const t = setTimeout(() => setNoticeReady(true), 600);
    return () => clearTimeout(t);
  }, [travel, noticeReady, enterAfterLaunch, entering, showShip]);

  const onLaunched = useCallback(() => {
    setLaunchHold(false);
    setTravel(true);
    setEnterAfterLaunch(true);
  }, []);

  const body: ReactNode =
    showShip && ship ? (
      <ShipHome
        ship={ship}
        variant={variant}
        go={go}
        markSeen={markSeen}
        launch={() => act('launch')}
        onFreeTalk={() => void navigate('/conversation')}
        onShowFullHome={() => setFullHome(true)}
        onSkip={
          ship.launchedAt
            ? () => void act('skip').catch((err: unknown) => console.warn('[ship] saltear', err))
            : undefined
        }
        onLaunchStart={() => setLaunchHold(true)}
        onLaunched={onLaunched}
      />
    ) : null;

  const intro: ReactNode = introOpen ? (
    <ShipIntro
      variant={variant}
      onStart={async () => {
        await act('start');
      }}
      onLater={async () => {
        await act('skip');
      }}
      onClosed={() => setIntroOpen(false)}
    />
  ) : null;

  const notice: ReactNode =
    travel === true && noticeReady && !noticeDone && !showShip ? (
      <ShipTravelNotice
        variant={variant}
        onShown={onNoticeShown}
        onOpen={() => void navigate('/profile')}
        onDone={() => setNoticeDone(true)}
      />
    ) : null;

  // La línea de arriba: «volver a la nave» mientras se mira el Inicio completo (si no, la rutina).
  const done = ship?.pieces.filter((p) => p.done).length ?? 0;
  const subhead: ReactNode =
    ship?.status === 'active' && fullHome ? (
      <div className="h2-subhead-row">
        <button
          type="button"
          className="h2-subhead h2-subhead--line ship-travel"
          onClick={() => setFullHome(false)}
        >
          <span
            className="ship-travel-rocket"
            dangerouslySetInnerHTML={{ __html: rocketSvg({ fire: false, label: '' }) }}
          />
          <span className="h2-today-tx">
            <b>Armá tu nave:</b> {done} de 7 piezas
          </span>
        </button>
      </div>
    ) : null;

  return {
    /** El Inicio espera esto en su hold-and-reveal (si no, la nave aparecería después). */
    resolved,
    body,
    intro,
    subhead,
    /** El aviso «Tu nave está en viaje» (flota sobre Perfil; se dibuja en el `body`). */
    notice,
  };
}
